import { createTheme } from "@mui/material/styles";
import { darkPalette, lightPalette } from "./palette";
import typography from "./typography";

// function to create theme based on mode
const getTheme = (mode = "dark") => {
  const selectedPalette = mode === "light" ? lightPalette : darkPalette;

  return createTheme({
    palette: selectedPalette,
    typography: typography,
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 992,
        lg: 1200,
        xl: 1536,
        xxl: 1760,
      },
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          elevation: {
            boxShadow: "0 0 15px rgba(204, 204, 204, 0.20)",
          },
          elevation3: {
            boxShadow: "5px 0px 15px rgba(204, 204, 204, 0.25)",
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableRipple: true,
        },
        styleOverrides: {
          root: {
            textTransform: "capitalize",
            borderRadius: 8,

            "&.Mui-disabled": {
              opacity: 0.5,
            },
          },

          sizeMedium: {
            padding: "8px 16px",
          },

          sizeSmall: {
            padding: "4px 8px",
          },

          containedPrimary: {
            border: "1px solid",
            borderColor: lightPalette.primary.main,

            "&.Mui-disabled": {
              borderColor: lightPalette.primary.main,
              backgroundColor: lightPalette.primary.main,
              color: lightPalette.common.white,
            },
          },

          outlinedPrimary: {
            ":hover": {
              backgroundColor: lightPalette.primary.main,
              color: lightPalette.common.white,
            },

            "&.Mui-disabled": {
              borderColor: lightPalette.primary.main,
              color: lightPalette.primary.main,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderColor: lightPalette.secondary.light,
            height: "auto",
            lineHeight: 1.5,

            "& legend": {
              fontSize: 14,
            },
            "&.MuiSelect-root": {
              padding: 0,
            },
          },

          input: {
            padding: "12px 16px",

            "::placeholder": {
              color: lightPalette.secondary.main,
            },
          },

          multiline: {
            padding: 0,

            "& .MuiInputBase-input": {
              padding: "12px 16px",
              resize: "vertical",
              minHeight: "48px",
            },
          },

          sizeSmall: {
            padding: "8px 12px",

            "& .MuiInputBase-input": {
              padding: 0,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: lightPalette.default.light,
            transform: "translate(14px, 12px) scale(1)",

            "+ .MuiInputBase-multiline.MuiOutlinedInput-root legend span": {
              maxWidth: "0px",
              width: 0,
              padding: 0,
            },

            "&.MuiInputLabel-shrink": {
              transform: "translate(16px, -9px) scale(0.875)",
              maxWidth: "100%",

              "+ .MuiInputBase-multiline.MuiOutlinedInput-root": {
                legend: {
                  maxWidth: "100%",
                  backgroundColor: lightPalette.background.paper,
                  visibility: "visible",

                  span: {
                    maxWidth: "100%",
                    width: "100%",
                    padding: "0 5px",
                  },
                },
              },
            },
          },
          sizeSmall: {
            transform: "translate(14px, 8px) scale(1)",

            "&.MuiInputLabel-shrink": {
              transform: "translate(16px, -9px) scale(0.875)",
            },
          },
        },
      },
      MuiFormGroup: {
        styleOverrides: {
          root: {
            "&.MuiRadioGroup-root": {
              gap: 8,

              ".MuiRadio-root": {
                paddingTop: 0,
                paddingBottom: 0,
              },
            },
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            alignItems: "flex-start",

            ".MuiCheckbox-root": {
              paddingTop: 0,
              paddingBottom: 0,
            },
          },
        },
      },
      MuiInputAdornment: {
        styleOverrides: {
          root: {
            color: lightPalette.secondary.light,

            ".MuiButtonBase-root": {
              padding: 0,
              marginRight: 0,
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            padding: 0,
          },
          outlined: {
            padding: "12px 16px",
            minHeight: "auto",
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          inputRoot: {
            padding: "12px 16px",

            "& .MuiInputBase-input": {
              padding: 0,
            },
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            paddingLeft: 12,
            paddingRight: 12,
            gap: 8,
            color: lightPalette.default.main,

            "&.active": {
              backgroundColor: lightPalette.primary.main,
              color: lightPalette.common.white,
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            margin: 0,
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: "unset",
            color: "inherit",
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          root: {
            fontSize: 14,
          },
        },
        defaultProps: {
          slotProps: {
            primary: {
              variant: "body2",
            },
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            ".MuiList-root": {
              paddingTop: 0,
              paddingBottom: 0,

              ".MuiMenuItem-root": {
                ":empty": {
                  height: 0,
                  padding: 0,
                },
              },
            },
          },
        },
      },
      MuiPopper: {
        styleOverrides: {
          root: {
            "&.MuiPickersPopper-root": {
              ".MuiList-root": {
                "::-webkit-scrollbar": {
                  width: 0 /* Remove scrollbar space */,
                  backgroundColor:
                    "transparent" /* Optional: just make scrollbar invisible */,
                },

                "&.MuiMultiSectionDigitalClockSection-root": {
                  "&::after": {
                    display: "none",
                  },
                },
              },
            },
          },
        },
      },
      MuiPickersTextField: {
        styleOverrides: {
          root: {
            color: "#1565c0",
            borderRadius: "2px",
            borderWidth: "1px",
            borderColor: "#2196f3",
            border: "1px solid",
            backgroundColor: "#90caf9",
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paperWidthXs: {
            maxWidth: 500,
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            gap: 16,
            padding: 24,
            borderTop: "1px solid",
            borderColor: lightPalette.default.lightest1,
          },
        },
        defaultProps: {
          disableSpacing: true,
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            padding: "16px 24px",
          },
        },
        defaultProps: {
          component: "div",
          typography: "h5",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid",
          borderColor: lightPalette.default.lightest1,
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: "24px !important",
          },
        },
      },
    },
  });
};

export default getTheme;
